import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import KpiTile from "@/components/KpiTile";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import { usePeriod } from "@/components/PeriodFilterBar";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

const LABEL = { DAY: "Today", WEEK: "Last 7 days", MTD: "Month to date",
                QTD: "Quarter to date", YTD: "Year to date (FY)", LTM: "Last 12 months" };

export default function Departments() {
  const { period, dept } = usePeriod();
  const q = `period=${period}${dept && dept !== "ALL" ? `&dept=${encodeURIComponent(dept)}` : ""}`;
  const { data } = useSWR(`/analysis/dept-leaderboard?${q}`, fetcher);
  const rows = data?.departments || [];

  // Derived metrics
  const totalSales = data?.total_sales || 0;
  const totalUnits = rows.reduce((s, r) => s + (r.units || 0), 0);
  const totalTxns = rows.reduce((s, r) => s + (r.transactions || 0), 0);
  const avgMargin = rows.length ? rows.reduce((s, r) => s + r.margin_pct, 0) / rows.length : 0;

  return (
    <div>
      <PageHeader
        eyebrow={`Sales Intelligence · Departments · ${LABEL[period] || period}${dept && dept !== "ALL" ? ` · ${dept}` : ""}`}
        title="Twelve floors, twelve stories."
        lede="Sales, margin and growth across the merchandising hierarchy."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <KpiTile label="Total sales" value={totalSales} accent />
        <KpiTile label="Units sold" kind="number" value={totalUnits} />
        <KpiTile label="Transactions" kind="number" value={totalTxns} />
        <KpiTile label="Avg margin" kind="pct" value={avgMargin} />
      </div>

      {/* Winners and losers */}
      <div className="grid md:grid-cols-2 gap-px bg-border mt-10">
        <div className="bg-background p-5">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-positive" /><span className="label-caps text-positive">Top movers</span></div>
          {(data?.winners || []).map((w) => (
            <div key={w.department} className="flex items-baseline justify-between py-1.5 hairline-b last:border-0">
              <span className="font-display text-base">{w.department}</span>
              <span className="font-mono text-sm text-positive">{formatPct(w.growth_pct, { signed: true })}</span>
            </div>
          ))}
        </div>
        <div className="bg-background p-5">
          <div className="flex items-center gap-2 mb-3"><TrendingDown className="w-4 h-4 text-destructive" /><span className="label-caps text-destructive">Sliding</span></div>
          {(data?.losers || []).map((w) => (
            <div key={w.department} className="flex items-baseline justify-between py-1.5 hairline-b last:border-0">
              <span className="font-display text-base">{w.department}</span>
              <span className="font-mono text-sm text-destructive">{formatPct(w.growth_pct, { signed: true })}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sales-by-department bar chart */}
      <Section eyebrow="Sales contribution" title="Where the rupee comes from">
        <div className="hairline p-4">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={rows} layout="vertical">
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
              <YAxis type="category" dataKey="department" stroke="hsl(var(--muted-foreground))" fontSize={10} width={140} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatIndianCurrency(v)} />
              <Bar dataKey="sales" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section eyebrow="The full table" title="Departments, ranked">
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Department</th>
                <th className="text-right">Sales</th>
                <th className="text-right">Share</th>
                <th className="text-right">Units</th>
                <th className="text-right">ATV</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Growth</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.department}>
                  <td className="font-display text-sm truncate max-w-[180px]" title={r.department}>{r.department}</td>
                  <td className="text-right">{formatIndianCurrency(r.sales)}</td>
                  <td className="text-right text-muted-foreground">{formatPct(r.share_pct)}</td>
                  <td className="text-right">{formatIndianGrouping(r.units)}</td>
                  <td className="text-right">{formatIndianCurrency(r.atv)}</td>
                  <td className="text-right">{formatPct(r.margin_pct)}</td>
                  <td className={`text-right ${r.growth_pct >= 0 ? "text-positive" : "text-destructive"}`}>{formatPct(r.growth_pct, { signed: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {rows.map((r) => (
            <div key={r.department} className="hairline p-4">
              <div className="font-display text-base">{r.department}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 font-mono text-xs">
                <div><div className="label-caps">Sales</div>{formatIndianCurrency(r.sales)}</div>
                <div><div className="label-caps">Share</div>{formatPct(r.share_pct)}</div>
                <div><div className="label-caps">Margin</div>{formatPct(r.margin_pct)}</div>
                <div><div className="label-caps">Growth</div><span className={r.growth_pct >= 0 ? "text-positive" : "text-destructive"}>{formatPct(r.growth_pct, { signed: true })}</span></div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
